import {
  ALL_GENERATORS,
  DEFAULT_GEN_SETTINGS,
  GEN_KINDS,
  MODEL_GENERATORS,
  PIXEL_GENERATORS,
  QUALITY_LABEL,
  SPEED_LABEL,
  VIDEO_EXTEND_OF,
  decider,
  defaultStageParams,
  generatorById,
  validateChain,
  validatePixelPipeline,
  HIRES_IMAGE_STAGES,
  pixelOutProblem,
  validatePipelineParams,
  type Decider,
  type GenKind,
  type GenRequest,
  type GenSettings,
  type GenStatus,
  type GenTypeSetting,
  type GeneratorInfo,
} from '@ai-studio/shared';

/**
 * Pure helpers for the local generation UI: the project AI settings file,
 * grouping of requests for the inbox, pipeline (chain) editing, request
 * bodies and catalog tolerance. No React, no network.
 */

export { AI_SETTINGS_FILE } from './ai-context.js';

/* ------------------------------------------------------------------ */
/* Settings file (.prism/ai-settings.json)                             */

function typeSetting(raw: unknown, fallback: GenTypeSetting): GenTypeSetting {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const allow = typeof r.allow === 'boolean' ? r.allow : fallback.allow;
  // "Only" cannot be on while local generation is not allowed.
  return {
    allow,
    only: allow && (typeof r.only === 'boolean' ? r.only : fallback.only),
    autoApprove: typeof r.autoApprove === 'boolean' ? r.autoApprove : fallback.autoApprove === true,
  };
}

/**
 * Normalises raw file contents into settings. Mirrors the worker's
 * parseAiSettings: older files with only `localModelGen` / `neverLocalModelGen`
 * map to the 3D-model `only` / not-`allow` switches.
 */
export function parseGenSettings(raw: unknown): GenSettings {
  const file = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const local = file.local && typeof file.local === 'object' ? (file.local as Record<string, unknown>) : {};
  const legacyNever = file.neverLocalModelGen === true;
  const legacyOnly = !legacyNever && file.localModelGen === true;
  const types = {} as Record<GenKind, GenTypeSetting>;
  for (const kind of GEN_KINDS) {
    const fallback = kind === 'model' ? { allow: !legacyNever, only: legacyOnly } : DEFAULT_GEN_SETTINGS.types[kind];
    types[kind] = typeSetting(local[kind], fallback);
  }
  return { types, autoApprove: file.autoApprove === true, autonomous: file.autonomous === true };
}

/** Parses the file text (empty / invalid text gives the defaults). */
export function parseGenSettingsText(text: string | null | undefined): GenSettings {
  if (!text) return parseGenSettings({});
  try {
    return parseGenSettings(JSON.parse(text));
  } catch {
    return parseGenSettings({});
  }
}

/** One type switched: turning Allow off also turns Only off. */
export function setTypeSetting(settings: GenSettings, kind: GenKind, patch: Partial<GenTypeSetting>): GenSettings {
  const next = { ...settings.types[kind], ...patch };
  if (!next.allow) next.only = false;
  return { ...settings, types: { ...settings.types, [kind]: next } };
}

export function setAutoApprove(settings: GenSettings, autoApprove: boolean): GenSettings {
  return { ...settings, autoApprove };
}

/**
 * Merges settings into the stored file object without losing other keys. The
 * `autonomous` mirror belongs to the server and is never written. Legacy keys
 * are kept in step only if they already exist, so older readers agree.
 */
export function mergeGenSettingsIntoFile(stored: unknown, settings: GenSettings): Record<string, unknown> {
  const file: Record<string, unknown> = stored && typeof stored === 'object' && !Array.isArray(stored) ? { ...(stored as Record<string, unknown>) } : {};
  const local: Record<string, unknown> = {};
  for (const kind of GEN_KINDS)
    local[kind] = {
      allow: settings.types[kind].allow,
      only: settings.types[kind].only,
      autoApprove: settings.types[kind].autoApprove === true,
    };
  file.local = { ...(file.local && typeof file.local === 'object' ? (file.local as Record<string, unknown>) : {}), ...local };
  file.autoApprove = settings.autoApprove;
  if ('localModelGen' in file) file.localModelGen = settings.types.model.allow && settings.types.model.only;
  if ('neverLocalModelGen' in file) file.neverLocalModelGen = !settings.types.model.allow;
  return file;
}

/** Who decides, given the switches (the Assistant's autonomous mode counts as `autonomous`). */
export function effectiveDecider(settings: Pick<GenSettings, 'autoApprove'>, autonomous: boolean): Decider {
  return decider({ autoApprove: settings.autoApprove, autonomous });
}

export const DECIDER_LABEL: Record<Decider, string> = {
  human: 'You approve',
  assistant: 'The assistant decides',
  auto: 'Everything is approved automatically',
};

export const DECIDER_NOTE: Record<Decider, string> = {
  human: 'Autonomous mode and auto-approve are off: you approve requests before they queue and accept, deny or redo each result.',
  assistant: 'Autonomous mode is on and auto-approve is off: the assistant approves requests and judges each result. This list only shows what is happening.',
  auto: 'Auto-approve is on: every request and result is approved automatically. With autonomous mode the assistant still refines the results.',
};

/** The four combinations of the two switches, for the settings popover. */
export const DECIDER_COMBOS: readonly { autonomous: boolean; autoApprove: boolean; title: string; text: string }[] = [
  { autonomous: false, autoApprove: false, title: 'Neither', text: 'You approve every request and accept, deny or redo every result.' },
  { autonomous: true, autoApprove: false, title: 'Autonomous only', text: 'The assistant decides: it approves requests and judges results, you only watch.' },
  { autonomous: false, autoApprove: true, title: 'Auto-approve only', text: 'Everything is approved automatically; nobody reviews results.' },
  { autonomous: true, autoApprove: true, title: 'Both', text: 'Everything is approved automatically; the assistant still refines and checks results on its own.' },
];

/* ------------------------------------------------------------------ */
/* Requests                                                            */

export interface QueueEntry {
  file: string;
  pid: number | string;
  kind: GenKind;
  label: string;
  at: string;
  position: number;
}

export interface GenerationsResponse {
  requests: GenRequest[];
  queue: QueueEntry[];
  decider: Decider;
}

export interface RequestGroups {
  /** Waiting for approval before they queue. */
  approval: GenRequest[];
  /** Finished, waiting for accept / deny / redo. */
  review: GenRequest[];
  /** Running, then queued (by position). */
  active: GenRequest[];
  /** Everything else (accepted, denied, rejected, failed, redo). */
  history: GenRequest[];
}

const HISTORY_STATUSES: readonly GenStatus[] = ['accepted', 'denied', 'rejected', 'failed', 'redo', 'refined'];

export function isHistoryStatus(status: GenStatus): boolean {
  return HISTORY_STATUSES.includes(status);
}

/** Splits requests (newest first) into the inbox sections. */
export function groupRequests(requests: readonly GenRequest[]): RequestGroups {
  const groups: RequestGroups = { approval: [], review: [], active: [], history: [] };
  for (const r of requests) {
    if (r.status === 'awaiting-approval') groups.approval.push(r);
    else if (r.status === 'review') groups.review.push(r);
    else if (r.status === 'running' || r.status === 'queued') groups.active.push(r);
    else groups.history.push(r);
  }
  groups.active.sort((a, b) => {
    if (a.status !== b.status) return a.status === 'running' ? -1 : 1;
    return (a.ahead ?? 0) - (b.ahead ?? 0);
  });
  return groups;
}

/** The requests that wait on the user: nothing unless the user is the decider. Approvals first. */
export function waitingItems(requests: readonly GenRequest[], who: Decider | null): GenRequest[] {
  if (who !== 'human') return [];
  const waiting = requests.filter((r) => r.status === 'awaiting-approval' || r.status === 'review');
  return [...waiting.filter((r) => r.status === 'awaiting-approval'), ...waiting.filter((r) => r.status === 'review')];
}

/** How many items wait on the user: nothing unless the user is the decider. */
export function waitingOnUser(requests: readonly GenRequest[], who: Decider | null): number {
  return waitingItems(requests, who).length;
}

/** Default stages of a refine: build on the kept result (null = a prompt tweak only). */
export function defaultRefineStages(kind: GenKind): string[] | null {
  if (kind === 'model') return ['trellis2'];
  if (kind === 'pixel') return ['cleanup'];
  return null;
}

export const STATUS_LABEL: Record<GenStatus, string> = {
  'awaiting-approval': 'Awaiting approval',
  rejected: 'Rejected',
  queued: 'Queued',
  running: 'Running',
  review: 'Ready for review',
  accepted: 'Accepted',
  denied: 'Denied',
  redo: 'Redone',
  refined: 'Built on',
  failed: 'Failed',
};

/** Last lines of a request's log. */
export function logTail(request: Pick<GenRequest, 'log'>, count = 4): string[] {
  return (request.log ?? []).slice(-count);
}

/** Best-effort 0..1 progress from a "stage 2/3 ..." style progress string; null if unknown. */
export function progressFraction(progress: string | undefined): number | null {
  if (!progress) return null;
  const pct = progress.match(/(\d{1,3})\s*%/);
  if (pct) return Math.min(1, Number(pct[1]) / 100);
  const frac = progress.match(/(\d+)\s*\/\s*(\d+)/);
  if (frac && Number(frac[2]) > 0) return Math.min(1, Number(frac[1]) / Number(frac[2]));
  return null;
}

/** The audio file of a finished audio request (project path). */
export function audioFileOf(request: Pick<GenRequest, 'files' | 'out'>): string {
  return request.files?.find((f) => /\.(wav|ogg|mp3|flac)$/i.test(f)) ?? request.out;
}

/** Which preview a review card shows. */
export type PreviewKind = 'audio' | 'model' | 'image' | 'pixel' | 'video' | 'none';

export function previewKind(request: Pick<GenRequest, 'kind' | 'out' | 'preview'>): PreviewKind {
  if (request.kind === 'speech' || request.kind === 'music' || request.kind === 'sfx') return 'audio';
  if (/\.(mp4|webm|mov)$/i.test(request.out)) return 'video';
  if (request.preview && /\.(png|jpe?g|webp|gif)$/i.test(request.preview)) return 'image';
  if (request.out.toLowerCase().endsWith('.pixel.json')) return 'pixel';
  if (/\.(model\.json|glb|gltf)$/i.test(request.out)) return 'model';
  return 'none';
}

/** The clip file of a finished video request (project path). */
export function videoFileOf(request: Pick<GenRequest, 'files' | 'out'>): string {
  return request.files?.find((f) => /\.(mp4|webm|mov)$/i.test(f)) ?? request.out;
}

/** "Build on the clip" choices of a video refine (extend with the same model, interpolate, upscale). */
export function videoRefineOptions(request: Pick<GenRequest, 'kind' | 'out' | 'stages'>): { value: string; label: string }[] {
  if (request.kind !== 'video' || !/\.mp4$/i.test(request.out)) return [];
  const first = request.stages[0] ?? '';
  if (first === 'assemble' || first === 'keyframe') return [];
  const extend = VIDEO_EXTEND_OF[request.stages.filter((s) => VIDEO_EXTEND_OF[s]).at(-1) ?? ''];
  const ids = [...(extend ? [extend] : []), 'interpolate', 'interpolate-film', 'upscale-fast', 'upscale'];
  return ids.map((id) => {
    const g = generatorById(id);
    return { value: id, label: g ? `${g.label} · ${SPEED_LABEL[g.speed] ?? ''} · ${QUALITY_LABEL[g.quality] ?? ''} quality` : id };
  });
}

/** "2 min ago" style age, from an ISO string. */
export function ageLabel(iso: string, now = Date.now()): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
}

/* ------------------------------------------------------------------ */
/* Generators and tags                                                 */

export function generatorsOf(kind: GenKind): readonly GeneratorInfo[] {
  return ALL_GENERATORS.filter((g) => g.kind === kind);
}

/** "TRELLIS.2 · Very slow · Best quality" (for dropdown options). */
export function generatorOptionLabel(g: GeneratorInfo): string {
  return `${g.label} · ${SPEED_LABEL[g.speed] ?? ''} · ${QUALITY_LABEL[g.quality] ?? ''} quality`;
}

/** Single model generators that give a mesh (a chain of one). */
export function meshGenerators(): readonly GeneratorInfo[] {
  return MODEL_GENERATORS.filter((g) => g.gives === 'mesh');
}

/** Label without the "(shape)" style suffix, for a compact chain line. */
export function shortLabel(g: GeneratorInfo): string {
  return g.label.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/** 'SDXL concept image → Hunyuan3D 2.0 → TRELLIS.2' */
export function chainLabel(ids: readonly string[]): string {
  return ids.map((id) => (generatorById(id) ? shortLabel(generatorById(id)!) : id)).join(' → ');
}

/** Rough total of the stage speeds: the slowest stage dominates. */
export function pipelineSpeed(ids: readonly string[]): number {
  const speeds = ids.map((id) => generatorById(id)?.speed).filter((s): s is number => typeof s === 'number');
  return speeds.length ? Math.min(...speeds) : 0;
}

/** The final stage decides the result's quality. */
export function pipelineQuality(ids: readonly string[]): number {
  const last = ids.length ? generatorById(ids[ids.length - 1]!) : undefined;
  return last?.quality ?? 0;
}

/* ------------------------------------------------------------------ */
/* Pipeline (chain) editing                                            */

export interface Pipeline {
  readonly stages: readonly string[];
  /** Per-stage settings, aligned with `stages`. */
  readonly params: readonly Record<string, unknown>[];
}

export const EMPTY_PIPELINE: Pipeline = { stages: [], params: [] };

export function pipelineOf(stages: readonly string[], params?: readonly Record<string, unknown>[]): Pipeline {
  return { stages: [...stages], params: stages.map((id, i) => ({ ...defaultStageParams(id), ...(params?.[i] ?? {}) })) };
}

export function addStage(p: Pipeline, id: string, index?: number): Pipeline {
  const at = index === undefined ? p.stages.length : Math.max(0, Math.min(index, p.stages.length));
  const stages = [...p.stages];
  const params = [...p.params];
  stages.splice(at, 0, id);
  params.splice(at, 0, { ...defaultStageParams(id) });
  return { stages, params };
}

export function removeStage(p: Pipeline, index: number): Pipeline {
  if (index < 0 || index >= p.stages.length) return p;
  return { stages: p.stages.filter((_, i) => i !== index), params: p.params.filter((_, i) => i !== index) };
}

/** Moves a stage by `delta` positions (-1 up, +1 down); out of range is a no-op. */
export function moveStage(p: Pipeline, index: number, delta: number): Pipeline {
  const to = index + delta;
  if (index < 0 || index >= p.stages.length || to < 0 || to >= p.stages.length || delta === 0) return p;
  const stages = [...p.stages];
  const params = [...p.params];
  [stages[index], stages[to]] = [stages[to]!, stages[index]!];
  [params[index], params[to]] = [params[to]!, params[index]!];
  return { stages, params };
}

export function setStageParam(p: Pipeline, index: number, key: string, value: unknown): Pipeline {
  if (index < 0 || index >= p.stages.length) return p;
  return { stages: p.stages, params: p.params.map((v, i) => (i === index ? { ...v, [key]: value } : v)) };
}

/** Replaces one stage's generator (keeps nothing of the old settings). */
export function replaceStage(p: Pipeline, index: number, id: string): Pipeline {
  if (index < 0 || index >= p.stages.length) return p;
  return {
    stages: p.stages.map((s, i) => (i === index ? id : s)),
    params: p.params.map((v, i) => (i === index ? { ...defaultStageParams(id) } : v)),
  };
}

/** `refine`: the stages build on a kept result (pixel: the PNG goes in, so no generator stage). */
export function validatePipeline(kind: 'model' | 'pixel', stages: readonly string[], refine = false): string | null {
  return kind === 'pixel' ? validatePixelPipeline(stages, { fromImage: refine }) : validateChain(stages);
}

/** Generators that can be appended to a pipeline right now. */
export function addableStages(kind: 'model' | 'pixel', stages: readonly string[], refine = false): readonly GeneratorInfo[] {
  if (kind === 'model') return MODEL_GENERATORS;
  return PIXEL_GENERATORS.filter((g) => (stages.length === 0 && !refine ? (g.role ?? 'generate') === 'generate' : (g.role ?? 'generate') !== 'generate'));
}

export function sameStages(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/* ------------------------------------------------------------------ */
/* Saved pixel pipelines (localStorage + JSON export / import)         */

export interface SavedPipeline {
  name: string;
  stages: string[];
  params: Record<string, unknown>[];
}

export const PIPELINE_EXPORT_FORMAT = 'prism.pixel-pipelines/1';

/** Keeps only known stages / valid entries; params are re-aligned with the stages. */
export function sanitizeSavedPipelines(raw: unknown): SavedPipeline[] {
  const list = Array.isArray(raw) ? raw : raw && typeof raw === 'object' && Array.isArray((raw as { pipelines?: unknown }).pipelines) ? (raw as { pipelines: unknown[] }).pipelines : [];
  const out: SavedPipeline[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const name = typeof r.name === 'string' ? r.name.trim() : '';
    if (!name || !Array.isArray(r.stages)) continue;
    const stages = r.stages.filter((s): s is string => typeof s === 'string' && PIXEL_GENERATORS.some((g) => g.id === s));
    if (!stages.length || stages.length !== r.stages.length) continue;
    const rawParams = Array.isArray(r.params) ? r.params : [];
    const params = stages.map((id, i) => {
      const given = rawParams[i] && typeof rawParams[i] === 'object' ? (rawParams[i] as Record<string, unknown>) : {};
      return { ...defaultStageParams(id), ...given };
    });
    out.push({ name, stages, params });
  }
  return out;
}

export function parseSavedPipelinesText(text: string): SavedPipeline[] {
  try {
    return sanitizeSavedPipelines(JSON.parse(text));
  } catch {
    return [];
  }
}

export function exportPipelinesText(list: readonly SavedPipeline[]): string {
  return `${JSON.stringify({ format: PIPELINE_EXPORT_FORMAT, pipelines: list }, null, 2)}\n`;
}

/** Adds or replaces (by name, case-insensitive). */
export function upsertPipeline(list: readonly SavedPipeline[], item: SavedPipeline): SavedPipeline[] {
  const key = item.name.trim().toLowerCase();
  const rest = list.filter((p) => p.name.trim().toLowerCase() !== key);
  return [...rest, { ...item, name: item.name.trim() }];
}

/* ------------------------------------------------------------------ */
/* Output paths and request bodies                                     */

export function slugify(text: string, fallback = 'untitled', max = 40): string {
  const slug = text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, max)
    .replace(/_+$/g, '');
  return slug || fallback;
}

/** Default output path for a new generation of `kind` from a prompt. */
export function defaultOutPath(kind: GenKind, prompt: string): string {
  const slug = slugify(prompt, kind === 'model' ? 'model' : kind === 'pixel' ? 'sprite' : kind);
  switch (kind) {
    case 'model':
      return `models/${slug}.model.json`;
    case 'pixel':
      return `art/ai/${slug}.pixel.json`;
    case 'video':
      return `videos/clips/${slug}.mp4`;
    default:
      return `sounds/ai/${kind}/${slug}.wav`;
  }
}

export interface GenerationBody {
  kind: GenKind;
  out: string;
  prompt?: string;
  text?: string;
  stages?: string[];
  stageParams?: Record<string, unknown>[];
  seed?: number;
  image?: string;
  [extra: string]: unknown;
}

/** Drops undefined / empty-string / NaN values so the server sees only what was set. */
export function compactBody<T extends Record<string, unknown>>(body: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) {
    if (v === undefined || v === null || v === '') continue;
    if (typeof v === 'number' && !Number.isFinite(v)) continue;
    out[k] = v;
  }
  return out as T;
}

export function modelRequestBody(options: { out: string; prompt: string; stages: readonly string[]; seed?: number }): GenerationBody {
  return compactBody({ kind: 'model' as const, out: options.out, prompt: options.prompt.trim(), stages: [...options.stages], seed: options.seed });
}

export function pixelRequestBody(options: { out: string; prompt: string; pipeline: Pipeline; seed?: number }): GenerationBody {
  return compactBody({
    kind: 'pixel' as const,
    out: options.out,
    prompt: options.prompt.trim(),
    stages: [...options.pipeline.stages],
    stageParams: options.pipeline.params.map((p) => ({ ...p })),
    seed: options.seed,
  });
}

export interface AudioFields {
  kind: 'speech' | 'music' | 'sfx';
  out: string;
  /** Speech: the line. Music / sfx: the description. */
  text: string;
  engine?: string;
  voice?: string;
  exaggeration?: number;
  speed?: number;
  lang?: string;
  style?: string;
  preset?: string;
  lyrics?: string;
  duration?: number;
  seed?: number;
}

export function audioRequestBody(f: AudioFields): GenerationBody {
  const base: Record<string, unknown> = { kind: f.kind, out: f.out, seed: f.seed };
  if (f.kind === 'speech') {
    Object.assign(base, { text: f.text.trim(), engine: f.engine, voice: f.voice, speed: f.speed, lang: f.lang });
    if (f.engine === 'chatterbox') base.exaggeration = f.exaggeration;
  } else if (f.kind === 'music') {
    Object.assign(base, { prompt: f.text.trim(), style: f.style, lyrics: f.lyrics, duration: f.duration });
  } else {
    Object.assign(base, { prompt: f.text.trim(), preset: f.preset, duration: f.duration });
  }
  return compactBody(base) as GenerationBody;
}

/** Why a request cannot be sent yet (null when fine). */
export function requestProblem(body: Pick<GenerationBody, 'kind' | 'out' | 'prompt' | 'text' | 'stages'> & { stageParams?: Record<string, unknown>[] }): string | null {
  if (!body.out.trim()) return 'Choose an output path.';
  if (body.kind === 'speech') return body.text?.trim() ? null : 'Type the line to speak.';
  if (body.kind === 'sfx' || body.kind === 'music') return body.prompt?.trim() || body.text?.trim() ? null : 'Describe what to generate.';
  if (!body.prompt?.trim()) return 'Describe what to generate.';
  if (body.kind === 'model') return validateChain(body.stages ?? []);
  return validatePixelPipeline(body.stages ?? []) ?? pixelOutProblem(body.stages ?? [], body.out) ?? validatePipelineParams(body.stages ?? [], body.stageParams);
}

/** Default output of a pixel pipeline: a hi-res image pipeline saves a plain PNG, pixel art a `.pixel.json`. */
export function defaultPixelOut(prompt: string, stages: readonly string[]): string {
  const hires = stages.some((id) => HIRES_IMAGE_STAGES.includes(id));
  const path = defaultOutPath('pixel', hires ? prompt || 'image' : prompt);
  return hires ? path.replace(/\.pixel\.json$/, '.png').replace('/sprite.png', '/image.png') : path;
}

/* ------------------------------------------------------------------ */
/* Catalog (tolerant)                                                  */

export interface Option {
  value: string;
  label: string;
}

/**
 * Voice lists arrive as an array of strings, an array of objects with id/name,
 * an object keyed by id, or groups (language -> list). Always yields options.
 */
export function normalizeOptions(raw: unknown, depth = 0): Option[] {
  const out: Option[] = [];
  const push = (value: unknown, label?: unknown) => {
    if (typeof value !== 'string' || !value) return;
    if (out.some((o) => o.value === value)) return;
    out.push({ value, label: typeof label === 'string' && label ? label : value });
  };
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === 'string') push(item);
      else if (item && typeof item === 'object') {
        const r = item as Record<string, unknown>;
        const id = r.id ?? r.voice ?? r.value ?? r.key ?? r.name;
        if (id !== undefined) push(String(id), r.label ?? r.name ?? r.title);
        else if (depth < 2) for (const o of normalizeOptions(Object.values(r), depth + 1)) push(o.value, o.label);
      }
    }
  } else if (raw && typeof raw === 'object') {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (Array.isArray(value) && depth < 2) {
        for (const o of normalizeOptions(value, depth + 1)) push(o.value, o.label);
      } else if (value && typeof value === 'object') {
        const r = value as Record<string, unknown>;
        push(key, r.label ?? r.name ?? r.title);
      } else if (typeof value === 'string') push(key, value);
      else push(key);
    }
  }
  return out;
}

export interface GenCatalog {
  kokoroVoices: Option[];
  chatterboxVoices: Option[];
  musicStyles: string[];
  sfxPresets: string[];
}

export function normalizeCatalog(raw: unknown): GenCatalog {
  const r = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const strings = (v: unknown) => normalizeOptions(v).map((o) => o.value);
  return {
    kokoroVoices: normalizeOptions(r.kokoroVoices),
    chatterboxVoices: normalizeOptions(r.chatterboxVoices),
    musicStyles: strings(r.musicStyles),
    sfxPresets: strings(r.sfxPresets),
  };
}

/** Previews that must stay crisp: pixel art (a pixel request, a `.pixel.json`, or a pixel pipeline stage). */
export function isPixelRequest(request: Pick<GenRequest, 'kind' | 'out' | 'stages'>): boolean {
  return request.kind === 'pixel' || request.out.toLowerCase().endsWith('.pixel.json') || (request.stages ?? []).some((s) => /pixel/i.test(s));
}

/** New width x height after editing one side with the aspect lock on (ratio = height / width). */
export function lockedSize(edit: 'w' | 'h', value: number, ratio: number, lo: number, hi: number): [number, number] {
  const clamp = (n: number) => Math.min(hi, Math.max(lo, n));
  const v = clamp(Math.round(value));
  if (!(ratio > 0)) return [v, v];
  return edit === 'w' ? [v, clamp(Math.round(v * ratio))] : [clamp(Math.round(v / ratio)), v];
}
